import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, ExternalLink, X } from "lucide-react";
import { InfoIcon } from "@/components/icons";
import { CircleCheckIcon } from "@/components/icons/CircleCheckIcon";
import { getUnavailableProductsWithReasons } from "@/hooks/useClinicProductAvailability";
import { trackEvent, getBookingItemCategory } from "@/utils/analytics";
import { useMembership } from "@/contexts/MembershipContext";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { languageMap, BOOKING_NS } from "@/i18n";
import { BookingLanguage } from "@/types/booking";
import { resolveClinicName, resolveProductKey } from "@/locales";

interface ClinicCardProps {
  clinic: {
    id: string;
    name: string;
    human_readable_id?: string | null;
    address: string;
    address_short?: string | null;
    description?: string | null;
    photo_url?: string | null;
    opening_hours?: Record<string, unknown>;
    operated_by?: string | null;
    google_maps_url?: string | null;
  };
  selectedClinic: string;
  selectedProducts: string[];
  availabilityData: Record<string, unknown>[];
  productsData: Record<string, unknown>[];
  onProductRemove: (productId: string) => void;
  onClinicSelect: (clinicId: string) => void;
  translations: {
    notAvailable: string;
    removeProduct: string;
    makeAvailable: string;
    unavailableSuffix: string;
    maps: string;
  };
}

export const ClinicCard = ({
  clinic,
  selectedClinic,
  selectedProducts,
  availabilityData,
  productsData,
  onProductRemove,
  onClinicSelect,
  translations,
}: ClinicCardProps) => {
  const { t: tBooking } = useTranslation(BOOKING_NS);
  const { isMember, isEntitledProduct, getEffectivePrice } = useMembership();
  const _products = useBookingFlowStore((s) => s._products);
  const selectedAddOns = useBookingFlowStore((s) => s.selectedAddOns);
  const language = useBookingFlowStore((s) => s.language);
  const allCartIds = [...selectedProducts, ...selectedAddOns];
  const allCartProducts = allCartIds
    .map((id) => _products.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const effectiveTotal = allCartProducts.reduce(
    (sum, p) => sum + getEffectivePrice(p),
    0,
  );
  const isRedemption =
    isMember &&
    effectiveTotal === 0 &&
    allCartProducts.some((p) => isEntitledProduct(p.stripe_product_id));
  const itemCategory = getBookingItemCategory(
    allCartIds,
    _products,
    isRedemption,
  );

  const unavailableProducts = React.useMemo(
    () =>
      getUnavailableProductsWithReasons(
        clinic.id,
        selectedProducts,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (availabilityData as any) || [],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (productsData as any) || [],
      ),
    [clinic.id, selectedProducts, availabilityData, productsData],
  );

  const hasUnavailableProducts = unavailableProducts.length > 0;
  const canSelectClinic = !hasUnavailableProducts;
  const isSelected = selectedClinic === clinic.id;
  const [unavailableExpanded, setUnavailableExpanded] = useState(false);

  const handleMapsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clinic.google_maps_url) {
      window.open(clinic.google_maps_url, "_blank");
    } else {
      const address = clinic.address_short || clinic.address;
      const query = `${clinic.name}, ${address}`;
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
        "_blank",
      );
    }
  };

  const handleSelectLocation = () => {
    if (canSelectClinic && !isSelected) {
      trackEvent({
        event: "clinic_select",
        props: {
          clinic: clinic.id,
          source: "clinic_card",
          item_category: itemCategory,
        },
      });
      onClinicSelect(clinic.id);
    }
  };

  return (
    <div
      className={`group relative h-full hover:z-10 ${
        canSelectClinic ? "cursor-pointer" : "cursor-not-allowed"
      }`}
      onClick={handleSelectLocation}
    >
      {/* Hover shadow underlay — fades in via opacity (fully GPU-composited),
          stays stable while the card scales. Keeps text crisp by avoiding
          a box-shadow repaint on the scaling layer. */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out shadow-card-lift"
      />
      <div
        className={`relative h-full rounded-2xl border-[1.6px] shadow-card-sm ${
          isSelected
            ? "bg-white border-aubergine-800"
            : "bg-white border-transparent"
        } ${hasUnavailableProducts ? "opacity-70" : ""}`}
      >
        <div className="absolute top-2 right-2 z-10 pointer-events-none flex items-center justify-center w-7 h-7">
          {isSelected ? (
            <CircleCheckIcon size={28} />
          ) : (
            <div className="w-[22px] h-[22px] rounded-full border-[1.5px] border-neutral-200 bg-white" />
          )}
        </div>

        {/* Unavailable pills — sit on the top edge of the card */}

        <div className="p-3 flex items-center gap-3">
          <div className="w-[88px] h-[88px] bg-neutral-100 rounded-[8px] flex-shrink-0 flex items-center justify-center overflow-hidden">
            {clinic.photo_url ? (
              <img
                src={clinic.photo_url}
                alt={resolveClinicName(language, clinic)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-neutral-400 rounded opacity-60" />
            )}
          </div>

          {/* pr-8 keeps the trailing info icon clear of the selection radio,
              which is absolutely positioned in the card's top-right corner. */}
          <div className="flex-1 min-w-0 pr-8">
            <h3 className="text-base font-medium text-neutral-900 leading-tight">
              {(() => {
                const words = resolveClinicName(language, clinic)
                  .trim()
                  .split(/\s+/);
                const last = words.pop() ?? "";
                const head = words.join(" ");
                return (
                  <>
                    {head && head + " "}
                    <span className="whitespace-nowrap">
                      {last}
                      <ClinicInfoPopover
                        openingHours={clinic.opening_hours}
                        operatedBy={clinic.operated_by}
                        language={language}
                      />
                    </span>
                  </>
                );
              })()}
            </h3>

            <p className="mt-1 text-sm text-neutral-500 leading-tight normal-case">
              {(() => {
                const addr = clinic.address || clinic.address_short || "";
                const lastComma = addr.lastIndexOf(",");
                const head =
                  lastComma > -1 ? addr.slice(0, lastComma + 1) : addr;
                const tail =
                  lastComma > -1 ? addr.slice(lastComma + 1).trim() : "";
                return (
                  <>
                    <span className="whitespace-nowrap">{head}</span>{" "}
                    <span className="whitespace-nowrap">
                      {tail}
                      <button
                        type="button"
                        onClick={handleMapsClick}
                        aria-label={tBooking("clinicStep.openInMaps")}
                        className="inline-flex items-center justify-center w-6 h-6 -my-1.5 ml-0.5 align-middle relative -top-0.5 rounded-full text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </>
                );
              })()}
            </p>

            {hasUnavailableProducts && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                {unavailableProducts.length === 1 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onProductRemove(unavailableProducts[0].id);
                    }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] normal-case text-left hover:bg-amber-100 transition-colors"
                  >
                    <span>
                      {resolveProductKey(language, unavailableProducts[0].name)}
                    </span>
                    <span className="text-amber-500/80">
                      {translations.unavailableSuffix}
                    </span>
                    <X className="h-2.5 w-2.5 opacity-70 ml-0.5 flex-shrink-0" />
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUnavailableExpanded((v) => !v);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] normal-case hover:bg-amber-100 transition-colors"
                    >
                      <span>
                        {tBooking("clinicStep.unavailableCount", {
                          count: unavailableProducts.length,
                        })}
                      </span>
                      <ChevronDown
                        className={`h-3 w-3 opacity-70 transition-transform duration-200 ${unavailableExpanded ? "rotate-180" : ""}`}
                      />
                    </button>

                    {unavailableExpanded && (
                      <div className="mt-1.5 flex flex-col gap-1">
                        {unavailableProducts.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onProductRemove(product.id);
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] normal-case text-left hover:bg-amber-100 transition-colors"
                          >
                            <span>
                              {resolveProductKey(language, product.name)}
                            </span>
                            <span className="text-amber-500/80">
                              {translations.unavailableSuffix}
                            </span>
                            <X className="h-2.5 w-2.5 opacity-70 ml-0.5 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

// Map day → its existing `clinicStep.<abbr>` translation key.
const DAY_TKEY: Record<(typeof DAY_KEYS)[number], string> = {
  monday: "clinicStep.mon",
  tuesday: "clinicStep.tue",
  wednesday: "clinicStep.wed",
  thursday: "clinicStep.thu",
  friday: "clinicStep.fri",
  saturday: "clinicStep.sat",
  sunday: "clinicStep.sun",
};

type DayHourRange = {
  open?: string;
  start?: string;
  close?: string;
  end?: string;
  closed?: boolean;
};
type DayHours = DayHourRange | DayHourRange[] | null | undefined;

function formatRanges(dh: DayHours, closedLabel: string): string[] {
  if (!dh) return [closedLabel];
  const fmt = (r: DayHourRange) => {
    const a = r?.open ?? r?.start;
    const b = r?.close ?? r?.end;
    return a && b ? `${a} - ${b}` : "";
  };
  if (Array.isArray(dh)) {
    const open = dh
      .filter((r) => !r?.closed && (r?.open || r?.start))
      .map(fmt)
      .filter(Boolean);
    return open.length === 0 ? [closedLabel] : open;
  }
  if (dh.closed) return [closedLabel];
  const s = fmt(dh);
  return s ? [s] : [closedLabel];
}

const ClinicInfoPopover: React.FC<{
  openingHours?: Record<string, unknown>;
  operatedBy?: string | null;
  language: BookingLanguage;
}> = ({ openingHours, operatedBy, language }) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const closedLabel = t("clinicStep.closed");
  const [open, setOpen] = React.useState(false);
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const scheduleOpen = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setOpen(true), 250);
  };
  const cancelOpen = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    setOpen(false);
  };
  const hours =
    typeof openingHours === "string"
      ? (() => {
          try {
            return JSON.parse(openingHours);
          } catch {
            return {};
          }
        })()
      : (openingHours as Record<string, DayHours>) || {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("clinicStep.clinicInfoAria")}
          onClick={(e) => {
            stop(e);
            if (openTimer.current) {
              clearTimeout(openTimer.current);
              openTimer.current = null;
            }
            setOpen((o) => !o);
          }}
          onMouseEnter={scheduleOpen}
          onMouseLeave={cancelOpen}
          className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors align-middle cursor-pointer relative -top-px outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
        >
          <InfoIcon size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        className="w-[400px] max-w-[calc(100vw-2rem)] border-0 bg-neutral-900 text-white text-sm leading-relaxed shadow-[0_8px_32px_-4px_rgba(0,0,0,0.18)] p-3.5"
        onClick={stop}
        onMouseEnter={() => {
          if (openTimer.current) {
            clearTimeout(openTimer.current);
            openTimer.current = null;
          }
          setOpen(true);
        }}
        onMouseLeave={cancelOpen}
      >
        {operatedBy && (
          <div className="mb-2.5">
            <div className="text-neutral-400 uppercase tracking-wider text-xs mb-1">
              {t("clinicStep.operatedBy")}
            </div>
            <div>{operatedBy}</div>
          </div>
        )}
        {Object.keys(hours).length > 0 && (
          <div>
            <div className="text-neutral-400 uppercase tracking-wider text-xs mb-1">
              {t("clinicStep.openingHours")}
            </div>
            <ul className="space-y-1">
              {(() => {
                const rows: { label: string; values: string[] }[] = [];
                const weekdays = DAY_KEYS.slice(0, 5);
                for (const d of weekdays) {
                  rows.push({
                    label: t(DAY_TKEY[d]),
                    values: hours[d]
                      ? formatRanges(hours[d], closedLabel)
                      : [closedLabel],
                  });
                }
                const satV = hours.saturday
                  ? formatRanges(hours.saturday, closedLabel)
                  : [closedLabel];
                const sunV = hours.sunday
                  ? formatRanges(hours.sunday, closedLabel)
                  : [closedLabel];
                if (
                  satV.length === sunV.length &&
                  satV.every((v, i) => v === sunV[i])
                ) {
                  rows.push({ label: t("clinicStep.satSun"), values: satV });
                } else {
                  rows.push({ label: t(DAY_TKEY.saturday), values: satV });
                  rows.push({ label: t(DAY_TKEY.sunday), values: sunV });
                }
                return rows.map((r) => (
                  <li key={r.label} className="flex justify-between gap-3">
                    <span className="text-neutral-300 flex-shrink-0">
                      {r.label}
                    </span>
                    <span className="text-right whitespace-nowrap">
                      {r.values.join(", ")}
                    </span>
                  </li>
                ));
              })()}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
